import React, { ComponentType, useEffect, useMemo } from 'react';
import {
	DataGridProProps,
	GridColDef,
	gridColumnsStateSelector,
	gridSortModelSelector,
	GridFilterModel as MuixGridFilterModel,
	GridSortModel,
	GridValidRowModel,
	useGridApiRef,
} from '@mui/x-data-grid-pro';
import { GridFilterModel, TablePinnedColumns, TableVisibility } from '@warehouse/object-browser/core';
import { CustomDataGridPro } from './CustomDataGridPro';
import { StyledDataGridProProps } from './styled-data-grid-pro';
import { muixGridVisibilityModelToTableVisibility, muixPinnedToTablePinned } from './muix.adapter';
import { TableColumnSort, TableManagerStore } from '../table-manager/table-manager.store';

export interface ServerDataGridSortAdapter<F extends string> {
	fromMuiXDataGridToApi(sortModel: GridSortModel): TableColumnSort<F>[];
	fromApiToMuiXDataGrid(sortModel: TableColumnSort<F>[]): GridSortModel;
}

export interface ServerDataGridFilterAdapter {
	fromMuiXDataGridToApi(filterModel: MuixGridFilterModel): GridFilterModel;
	fromApiToMuiXDataGrid(filterModel: GridFilterModel): MuixGridFilterModel;
}

export interface ServerDataGridSelector<S, F extends string> {
	sort: (state: S) => TableColumnSort<F>[];
	filter: (state: S) => GridFilterModel;
	visibility: (state: S) => TableVisibility;
	pinned: (state: S) => TablePinnedColumns;
	order: (state: S) => string[];
	actions: (
		state: S,
	) => Pick<TableManagerStore<F>['actions'], 'setSort' | 'setFilter' | 'setVisibility' | 'setPinned' | 'setOrder'>;
}

export interface ServerDataGridConfig<F extends string> {
	sorting: false | { adapter: ServerDataGridSortAdapter<F> };
	filtering: false | { adapter: ServerDataGridFilterAdapter };
	pinning: 'store' | 'grid';
	reorder: boolean;
	resizable: boolean;
	pagination: { placement: 'in-grid' | 'below'; component: ComponentType };
}

type GridOwnedProps =
	| 'sortingMode'
	| 'sortModel'
	| 'onSortModelChange'
	| 'filterMode'
	| 'filterModel'
	| 'onFilterModelChange'
	| 'columnVisibilityModel'
	| 'onColumnVisibilityModelChange'
	| 'pinnedColumns'
	| 'onPinnedColumnsChange'
	| 'pagination'
	| 'hideFooter'
	| 'disableColumnReorder'
	| 'initialState';

type GridComponentProps<R extends GridValidRowModel> = DataGridProProps<R> & StyledDataGridProProps;

export type ServerDataGridProps<R extends GridValidRowModel, F extends string, S> = Omit<
	GridComponentProps<R>,
	GridOwnedProps
> & {
	useStore: <T>(selector: (state: S) => T) => T;
	selector: ServerDataGridSelector<S, F>;
	config: ServerDataGridConfig<F>;
	gridComponent?: ComponentType<GridComponentProps<R>>;
};

export function ServerDataGrid<R extends GridValidRowModel, F extends string, S>({
	useStore,
	selector,
	config,
	columns,
	slots,
	apiRef: apiRefFromProps,
	gridComponent,
	...rest
}: ServerDataGridProps<R, F, S>) {
	const { sorting, filtering, pinning, reorder, resizable, pagination } = config;
	const sort = useStore(selector.sort);
	const filter = useStore(selector.filter);
	const visibility = useStore(selector.visibility);
	const pinned = useStore(selector.pinned);
	const order = useStore(selector.order);
	const { setSort, setFilter, setVisibility, setPinned, setOrder } = useStore(selector.actions);

	const GridComponent = gridComponent ?? CustomDataGridPro;
	const internalApiRef = useGridApiRef();
	const apiRef = apiRefFromProps ?? internalApiRef;

	const gridColumns = useMemo(
		() =>
			columns.map(
				(column): GridColDef<R> => ({
					...column,
					...(!sorting && { sortable: false }),
					...(!filtering && { filterable: false }),
					...(!resizable && { resizable: false }),
				}),
			),
		[columns, sorting, filtering, resizable],
	);

	const gridColumnsState = apiRef.current?.state ? gridColumnsStateSelector(apiRef.current.state) : undefined;

	/**
	 * This use-effect synchronizes the field order in the store and the internal field order maintained by the table.
	 * There is a known issue where there is small lag (~500ms) between the moment the reordering (through dnd or pinning)
	 * takes place and the moment the useEffect fires. We consider this an acceptable tradeoff. Push back against
	 * any request to "bugfix" this.
	 */
	useEffect(() => {
		if (reorder && gridColumnsState) {
			setOrder(gridColumnsState.orderedFields);
		}
	}, [reorder, gridColumnsState, gridColumnsState?.orderedFields, setOrder]);

	const PaginationComponent = pagination.component;
	const isPaginationInGrid = pagination.placement === 'in-grid';

	const grid = (
		<GridComponent
			{...rest}
			apiRef={apiRef}
			columns={gridColumns}
			initialState={{
				...(pinning === 'store' && { pinnedColumns: { left: [...(pinned.left || [])] } }),
				...(sorting && { sorting: { sortModel: sorting.adapter.fromApiToMuiXDataGrid(sort) } }),
				columns: {
					columnVisibilityModel: visibility,
					...(reorder && { orderedFields: order }),
				},
				...(filtering && { filter: { filterModel: filtering.adapter.fromApiToMuiXDataGrid(filter) } }),
			}}
			slots={isPaginationInGrid ? { ...slots, pagination: PaginationComponent } : slots}
			pagination={isPaginationInGrid || undefined}
			hideFooter={!isPaginationInGrid || undefined}
			disableColumnReorder={!reorder}
			onColumnVisibilityModelChange={(model) => {
				setVisibility(muixGridVisibilityModelToTableVisibility(model));
				const sortModel = gridSortModelSelector(apiRef);
				const visibleSortModel = sortModel.filter((sortItem) => model[sortItem.field] !== false);
				if (visibleSortModel.length < sortModel.length) apiRef.current.setSortModel(visibleSortModel);
			}}
			{...(pinning === 'store' && {
				onPinnedColumnsChange: (pinnedColumns) => setPinned(muixPinnedToTablePinned(pinnedColumns)),
			})}
			{...(sorting && {
				sortingMode: 'server',
				onSortModelChange: (sortModel) => setSort(sorting.adapter.fromMuiXDataGridToApi(sortModel)),
			})}
			{...(filtering && {
				filterMode: 'server',
				onFilterModelChange: (filterModel) => setFilter(filtering.adapter.fromMuiXDataGridToApi(filterModel)),
			})}
		/>
	);

	if (isPaginationInGrid) return grid;

	return (
		<>
			{grid}
			<PaginationComponent />
		</>
	);
}
